import { jwtDecode } from 'jwt-decode';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.geonexs.com';

class AuthService {
  setTokens(accessToken, refreshToken) {
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  getToken() {
    return localStorage.getItem('authToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  removeTokens() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      this.removeTokens();
      return false;
    }
  }

  getUserRole() {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.user_role || null;
    } catch (error) {
      return null;
    }
  }

  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return { success: false, error: 'No refresh token available' };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        this.logout();
        return { success: false, error: 'Failed to refresh token' };
      }

      const data = await response.json();
      this.setTokens(data.access_token, data.refresh_token);
      return { success: true };
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      return { success: false, error: error.message };
    }
  }

  async getCurrentUser() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await this.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
              },
            });
            
            if (!retryResponse.ok) {
              this.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
            
            const userData = await retryResponse.json();
      
            return this.formatUserProfile(userData);
          } else {
            this.logout();
            window.location.href = '/login';
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();

      return this.formatUserProfile(userData);
    } catch (error) {
      throw error;
    }
  }

  formatUserProfile(userData) {
    if (!userData) return null;

    const id = userData.profile?.id || userData.id || userData.user_id;

    return {
      ...userData,
      id: id,
      username: userData.username || userData.name || '',
      email: userData.email || '',
      phone_number: userData.phone_number || '',
      role: userData.role || userData.user_role || '',
      role_display: userData.role_display || '',
      company_name: userData.company_name || '',
      registration_number: userData.registration_number || '',
      contact_person: userData.contact_person || '',
      address: userData.address || '',
      created_at: userData.created_at || userData.join_date
    };
  }

  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      this.setTokens(data.access_token, data.refresh_token);
      
      const formattedProfile = data.profile ? this.formatUserProfile({
        ...data,
        ...data.profile,
        role: data.user_role || data.role,
        username: data.username || data.profile?.username || username,
        email: data.email || data.profile?.email || '',
        phone_number: data.phone_number || data.profile?.phone_number || ''
      }) : null;
 
      return { 
        success: true, 
        userRole: data.user_role,
        userProfile: formattedProfile
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async logout() {
    const refreshToken = this.getRefreshToken();
    
    if (refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch (error) {
      }
    }
    
    this.removeTokens();
  }

  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reset password');
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}

export default new AuthService();